import React from 'react';
import * as R from 'ramda';
// components
import { TextComponent } from '../../../text';
import { TimePickerMui } from '../../../timepicker-mui';
import { reportBooleanValues, REPORT_FILTER_DATE_OPERATION_SPECIFIC_DAY } from '../../../filter/settings';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
import { specificDayOptions } from '../../../../helpers/options';
// forms
import { DatePickerMui } from '../../../datepicker-mui';
import {
  Input,
  FormGroup,
  DatePicker,
  CalendarFormGroup,
} from '../../../../forms';
// ui
import { Box, ReactSelect, CreatableReactSelect } from '../../../../ui';
// component edit-report
import { setOptions } from '../../helpers';
import { Option, FilterDate } from '../../ui';
//////////////////////////////////////////////////

const FilterOptionComponent = (props) => {
  const { data, action, selectOption } = props;

  return (
    <Option
      onClick={(e) => {
        selectOption(data, e);
        action(props);
      }}
    >
      {data.name}
    </Option>
  );
};

const ValueComponent = (props) => {
  const value = R.path(['data', 'name'], props);

  return (
    <TextComponent
      my={6}
      ml='8px'
      title={value}
      withEllipsis={true}
      position='absolute'
      width='calc(100% - 8px)'
    >
      {value}
    </TextComponent>
  );
};

export const FilterSelect = (({
  zI,
  id,
  name,
  value,
  margin,
  action,
  options,
  groupId,
  dataType,
  isDisabled,
  collection,
  filterIndex,
  placeholder,
  withoutOrGroup,
  filterFieldWidth,
  useMenuPortalTarget,
  filterSelectMaxMenuHeight,
}) => (
  <Box
    zI={zI}
    margin={R.or(margin, '0 10px')}
    width={R.or(filterFieldWidth, 232)}
  >
    <ReactSelect
      id={id}
      name={name}
      height={30}
      value={value}
      action={action}
      isClearable={false}
      isDisabled={isDisabled}
      onBlurResetsInput={false}
      placeholder={placeholder}
      onSelectResetsInput={false}
      options={setOptions(options)}
      maxMenuHeight={filterSelectMaxMenuHeight}
      useMenuPortalTarget={useMenuPortalTarget}
      closeMenuOnScroll={R.pathEq('edit_report_wrapper', ['target', 'id'])}
      components={{
        SingleValue: ValueComponent,
        Option: (props) => (
          <FilterOptionComponent
            {...props}
            action={action}
            groupId={groupId}
            dataType={dataType}
            collection={collection}
            filterIndex={filterIndex}
            withoutOrGroup={withoutOrGroup}
          />
        ),
      }}
    />
  </Box>
));

const BooleanComponent = (props) => {
  const { id, groupId, isDisabled, filterIndex, booleanValue, handleChangeSelect, useMenuPortalTarget } = props;

  return (
    <FilterSelect
      id={id}
      groupId={groupId}
      value={booleanValue}
      isDisabled={isDisabled}
      filterIndex={filterIndex}
      options={reportBooleanValues()}
      useMenuPortalTarget={useMenuPortalTarget}
      action={(values) => handleChangeSelect({ values, filterProp: 'booleanValue' })}
    />
  );
};

const getInputNumber = (event) => {
  const value = R.path(['target', 'value'], event);

  if (G.isNilOrEmpty(value)) return null;

  return G.toNumber(value);
};

const NumberComponent = (props) => {
  const {
    groupId,
    operation,
    isDisabled,
    filterIndex,
    numberValue,
    numberRange,
    handleChangeInput,
    handleChangeRange } = props;

  if (R.equals(operation, 'range')) {
    return (
      <FormGroup direction='row'>
        {
          ['from', 'to'].map((prefix, i) => (
            <Input
              key={i}
              min={0}
              m='0 10px'
              width={106}
              type='number'
              disabled={isDisabled}
              value={R.pathOr('', [prefix], numberRange)}
              placeholder={G.getWindowLocale('titles:enter-value', 'Enter a Value')}
              onChange={(event) => handleChangeRange({
                groupId,
                filterIndex,
                rangeType: prefix,
                filterProp: 'numberRange',
                value: getInputNumber(event),
              })}
            />
          ))
        }
      </FormGroup>
    );
  }

  return (
    <FormGroup>
      <Input
        min={0}
        m='0 10px'
        width={232}
        type='number'
        value={numberValue}
        disabled={isDisabled}
        placeholder={G.getWindowLocale('titles:enter-value', 'Enter a Value')}
        onChange={(event) => (
          handleChangeInput({ groupId, filterIndex, filterProp: 'numberValue', value: getInputNumber(event) })
        )}
      />
    </FormGroup>
  );
};

const StringComponent = (props) => {
  const {
    groupId,
    operation,
    isDisabled,
    filterIndex,
    stringValue,
    stringRange,
    handleChangeInput,
    handleChangeRange } = props;

  if (R.equals(operation, 'range')) {
    return (
      <FormGroup direction='row'>
        {
          ['from', 'to'].map((prefix, i) => (
            <Input
              key={i}
              m='0 10px'
              type='text'
              width={106}
              disabled={isDisabled}
              value={R.pathOr('', [prefix], stringRange)}
              placeholder={G.getWindowLocale('titles:enter-value', 'Enter a Value')}
              onChange={(event) => handleChangeRange({
                groupId,
                filterIndex,
                rangeType: prefix,
                filterProp: 'stringRange',
                value: event.target.value,
              })}
            />
          ))
        }
      </FormGroup>
    );
  }

  return (
    <FormGroup>
      <Input
        min={0}
        m='0 10px'
        type='text'
        width={232}
        value={stringValue}
        disabled={isDisabled}
        placeholder={G.getWindowLocale('titles:enter-value', 'Enter a Value')}
        onChange={(event) => (
          handleChangeInput({ groupId, filterIndex, filterProp: 'stringValue', value: event.target.value })
        )}
      />
    </FormGroup>
  );
};

const ReferenceComponent = (props) => {
  const {
    groupId,
    isDisabled,
    filterIndex,
    referenceValue,
    handleChangeInput } = props;

  return (
    <FormGroup>
      <Input
        min={0}
        m='0 10px'
        type='text'
        width={232}
        disabled={isDisabled}
        value={referenceValue}
        placeholder={G.getWindowLocale('titles:enter-value', 'Enter a Value')}
        onChange={(event) => (
          handleChangeInput({ groupId, filterIndex, filterProp: 'referenceValue', value: event.target.value })
        )}
      />
    </FormGroup>
  );
};

export const CalendarGroup = (props) => {
  const {
    value,
    width,
    maxDate,
    minDate,
    handler,
    disabled,
    direction,
    placeholder,
    isClearable,
    timeIntervals,
  } = props;

  const selected = G.isNotNilAndNotEmpty(value) ? new Date(value) : null;

  const setMaxDate = () => {
    if (G.isNotNilAndNotEmpty(maxDate)) {
      return maxDate;
    }

    return null;
  };

  const setMinDate = () => {
    if (G.isNotNilAndNotEmpty(minDate)) {
      return minDate;
    }

    return null;
  };

  return (
    <CalendarFormGroup direction={R.or(direction, 'row')}>
      <DatePicker
        pl='3px'
        m='0 10px'
        fontSize={10}
        width={width}
        autoComplete='off'
        onChange={handler}
        disabled={disabled}
        selected={selected}
        todayButton='Today'
        maxDate={setMaxDate()}
        minDate={setMinDate()}
        showYearDropdown={true}
        showMonthDropdown={true}
        dateFormat={GC.DEFAULT_DATE_FORMAT}
        isClearable={R.or(isClearable, false)}
        timeIntervals={R.or(timeIntervals, 15)}
        placeholderText={R.or(placeholder, '')}
      />
    </CalendarFormGroup>
  );
};

export const CalendarGroupMui = ({
  m,
  value,
  width,
  maxDate,
  minDate,
  handler,
  disabled,
  placeholder,
  disableMuiDatePortal = true,
}) => {
  const setMaxDate = () => {
    if (G.isNotNilAndNotEmpty(maxDate)) {
      return maxDate;
    }
    return G.dateAddYearsFromCurrent(100);
  };
  const setMinDate = () => {
    if (G.isNotNilAndNotEmpty(minDate)) {
      return minDate;
    }
    return G.dateSubtractYearsFromCurrent(100);
  };

  return (
    <DatePickerMui
      m={m}
      width={width}
      value={value}
      onChange={handler}
      disabled={disabled}
      maxDate={setMaxDate()}
      minDate={setMinDate()}
      withCalendarIcon={true}
      placeholder={placeholder}
      disablePortal={disableMuiDatePortal}
      inputSettings={{
        fontSize: 12,
        p: '0 0 0 3px',
        zIndex: 'unset',
      }}
    />
  );
};

const DateComponent = (props) => {
  const {
    id,
    groupId,
    timeUnit,
    isDisabled,
    stringValue,
    filterIndex,
    dateRelative,
    handleChangeDate,
    useMenuPortalTarget,
    disableMuiDatePortal,
  } = props;

  if (G.isTrue(dateRelative)) {
    const lastNextValues = R.pick(['last', 'next'], props);

    return (
      <FilterDate zI='0'>
        {
          ['last', 'next'].map((prefix, i) => (
            <Input
              key={i}
              m='0 10px'
              width={106}
              type='number'
              disabled={isDisabled}
              value={R.pathOr('', [prefix], lastNextValues)}
              placeholder={G.getWindowLocale(`titles:${prefix}`, prefix)}
              onChange={(event) => handleChangeDate({
                groupId,
                filterIndex,
                rangeType: prefix,
                filterProp: 'dateRelative',
                value: event.target.value,
              })}
            />
          ))
        }
      </FilterDate>
    );
  }

  if (R.equals(timeUnit, REPORT_FILTER_DATE_OPERATION_SPECIFIC_DAY)) {
    return (
      <Box width={232} m='0 10px'>
        <ReactSelect
          id={id}
          height={30}
          isClearable={false}
          value={stringValue}
          menuPlacement='auto'
          isDisabled={isDisabled}
          options={specificDayOptions}
          menuShouldScrollIntoView={false}
          useMenuPortalTarget={useMenuPortalTarget}
          closeMenuOnScroll={R.pathEq('edit_report_wrapper', ['target', 'id'])}
          onChange={({ value }) => handleChangeDate({ value, groupId, filterIndex, filterProp: 'stringValue' })}
        />
      </Box>
    );
  }

  const fromToValues = R.pick(['from', 'to'], props);

  const configUseMuiCalendar = G.getAmousConfigByNameFromWindow(GC.UI_USE_MUI_CALENDAR);
  const CalendarGroupToUse = G.ifElse(G.isTrue(configUseMuiCalendar), CalendarGroupMui, CalendarGroup);

  return (
    <FilterDate zI='unset'>
      {
        ['from', 'to'].map((prefix, i) => {
          let maxDate;
          let minDate;

          const isFrom = R.and(R.equals(prefix, 'from'), G.isNotNilAndNotEmpty(fromToValues.to));

          if (isFrom) {
            maxDate = G.isValidDate(fromToValues.to) ? new Date(fromToValues.to) : null;
          }

          if (R.equals(prefix, 'to')) {
            minDate = G.isValidDate(fromToValues.from) ? new Date(fromToValues.from) : null;
          }

          return (
            <CalendarGroupToUse
              key={i}
              m='0 10px'
              width={150}
              maxDate={maxDate}
              minDate={minDate}
              disabled={isDisabled}
              disableMuiDatePortal={disableMuiDatePortal}
              value={R.pathOr('', [prefix], fromToValues)}
              placeholder={
                `${G.getWindowLocale('titles:select-date', 'Select Date')} ${
                  G.getWindowLocale(`titles:${prefix}`, prefix)}`
              }
              handler={(date) => {
                const value = G.ifElse(
                  R.equals(prefix, 'from'),
                  G.createLocalDateTimeFromInstanceOrISOString(date, G.getDateTimeFormat(true)),
                  G.toEndOfDayFromDate(date),
                );

                handleChangeDate({
                  value,
                  groupId,
                  filterIndex,
                  rangeType: prefix,
                  filterProp: 'dateRange',
                });
              }}
            />
          );
        })
      }
    </FilterDate>
  );
};

const DateOnlyComponent = (props) => {
  const {
    to,
    from,
    operation,
    groupId,
    isDisabled,
    filterIndex,
    handleChangeDate,
    disableMuiDatePortal,
  } = props;

  const dateValues = { to, from };
  const prefixes = R.equals(operation, 'equal') ? ['from'] : ['from', 'to'];
  const configUseMuiCalendar = G.getAmousConfigByNameFromWindow(GC.UI_USE_MUI_CALENDAR);
  const CalendarGroupToUse = G.ifElse(G.isTrue(configUseMuiCalendar), CalendarGroupMui, CalendarGroup);

  return (
    <FilterDate zI='unset'>
      {
        prefixes.map((prefix) => {
          const isFrom = R.equals(prefix, 'from');
          const isRange = R.equals(operation, GC.FIELD_DATE_RANGE);
          const maxDate = G.isAllTrue(isRange, isFrom, G.isValidDate(to)) ? new Date(to) : null;
          const minDate = G.isAllTrue(isRange, R.not(isFrom), G.isValidDate(from)) ? new Date(from) : null;

          return (
            <CalendarGroupToUse
              key={prefix}
              m='0 10px'
              maxDate={maxDate}
              minDate={minDate}
              disabled={isDisabled}
              width={G.ifElse(isRange, 150, 232)}
              value={R.propOr('', prefix, dateValues)}
              disableMuiDatePortal={disableMuiDatePortal}
              placeholder={isRange
                ? `${G.getWindowLocale('titles:select-date', 'Select Date')} ${
                  G.getWindowLocale(`titles:${prefix}`, prefix)}`
                : G.getWindowLocale('titles:select-date', 'Select Date')
              }
              handler={(date) => handleChangeDate({
                groupId,
                filterIndex,
                rangeType: prefix,
                filterProp: 'dateOnlyRange',
                value: R.isNil(date) ? null : G.convertInstanceToDefaultDateFormat(date),
              })}
            />
          );
        })
      }
    </FilterDate>
  );
};

const TimeComponent = (props) => {
  const {
    to,
    from,
    groupId,
    isDisabled,
    filterIndex,
    handleChangeDate,
  } = props;

  const timeValues = { to, from };

  return (
    <FilterDate zI='unset'>
      {
        ['from', 'to'].map((prefix) => (
          <TimePickerMui
            m='0 10px'
            width={150}
            key={prefix}
            disabled={isDisabled}
            value={R.propOr('', prefix, timeValues)}
            placeholder={`${G.getWindowLocale('titles:select-time', 'Select Time')} ${
              G.getWindowLocale(`titles:${prefix}`, prefix)}`}
            onChange={(value) => handleChangeDate({
              value,
              groupId,
              filterIndex,
              rangeType: prefix,
              filterProp: 'timeRange',
            })}
          />
        ))
      }
    </FilterDate>
  );
};

const getMultiSelectValue = (selectMultipleValue, stringValue) => {
  if (G.isNotNilAndNotEmpty(selectMultipleValue)) {
    return selectMultipleValue;
  } else if (G.isNotNilAndNotEmpty(stringValue)) {
    return R.split(',', stringValue);
  }

  return [];
};

const MultipleSelectComponent = (props) => {
  const {
    id,
    groupId,
    isDisabled,
    filterIndex,
    stringValue,
    propertyName,
    useMenuPortalTarget,
    selectMultipleValue,
    handleChangeMultiSelect,
    filterSelectMaxMenuHeight,
  } = props;

  // TODO: check cond logic
  let searchOptions;

  if (G.isNilOrEmpty(R.path(['filterBy'], props))) {
    searchOptions = R.find(R.propEq(propertyName, 'value'))(R.path(['availableFields'], props));
  } else {
    searchOptions = R.find(R.propEq(propertyName, 'name'))(R.path(['filterBy'], props));
  }

  const { options, placeholder, handleDisableOptions } = searchOptions;

  let optionsToUse = R.or(options, []);

  if (G.isFunction(options)) optionsToUse = options();

  if (G.isNilOrEmpty(R.path([0, GC.FIELD_VALUE], optionsToUse))) {
    optionsToUse = R.tail(optionsToUse);
  }

  let placeholderValue = '';

  if (G.isNotNilAndNotEmpty(placeholder)) {
    placeholderValue = G.getWindowLocale(placeholder.key, placeholder.text);
  }

  return (
    <FormGroup>
      <Box m='5px 10px' width={232}>
        <ReactSelect
          id={id}
          height={30}
          isMulti={true}
          menuPlacement='auto'
          options={optionsToUse}
          isDisabled={isDisabled}
          placeholder={placeholderValue}
          menuShouldScrollIntoView={false}
          shouldNotGetValueFromOptions={true}
          isOptionDisabled={handleDisableOptions}
          maxMenuHeight={filterSelectMaxMenuHeight}
          useMenuPortalTarget={useMenuPortalTarget}
          value={getMultiSelectValue(selectMultipleValue, stringValue)}
          closeMenuOnScroll={R.pathEq('edit_report_wrapper', ['target', 'id'])}
          onChange={(options) => handleChangeMultiSelect({ options, groupId, filterIndex })}
        />
      </Box>
    </FormGroup>
  );
};

const SingleSelectComponent = (props) => {
  const {
    id,
    groupId,
    isDisabled,
    filterIndex,
    stringValue,
    propertyName,
    handleChangeInput,
    useMenuPortalTarget,
  } = props;

  let searchOptions;

  if (G.isNilOrEmpty(R.path(['filterBy'], props))) {
    searchOptions = R.find(R.propEq(propertyName, 'value'))(R.path(['availableFields'], props));
  } else {
    searchOptions = R.find(R.propEq(propertyName, 'name'))(R.path(['filterBy'], props));
  }

  const { options, placeholder } = searchOptions;

  return (
    <Box width={232} m='0 10px'>
      <ReactSelect
        id={id}
        height={30}
        options={options}
        isClearable={false}
        value={stringValue}
        menuPlacement='auto'
        isDisabled={isDisabled}
        menuShouldScrollIntoView={false}
        useMenuPortalTarget={useMenuPortalTarget}
        placeholder={G.getWindowLocale(placeholder.key, placeholder.text)}
        closeMenuOnScroll={R.pathEq('edit_report_wrapper', ['target', 'id'])}
        onChange={({ value }) => handleChangeInput({ value, groupId, filterIndex, filterProp: 'stringValue' })}
      />
    </Box>
  );
};

const MultipleSelectComponentCreatable = (props) => {
  const {
    id,
    groupId,
    isDisabled,
    filterIndex,
    stringValue,
    propertyName,
    useMenuPortalTarget,
    selectMultipleValue,
    handleChangeMultiSelect,
    filterSelectMaxMenuHeight,
  } = props;

  let searchOptions;

  if (G.isNilOrEmpty(R.path(['filterBy'], props))) {
    searchOptions = R.find(R.propEq(propertyName, 'value'))(R.path(['availableFields'], props));
  } else {
    searchOptions = R.find(R.propEq(propertyName, 'name'))(R.path(['filterBy'], props));
  }

  const { options, placeholder, handleDisableOptions } = searchOptions;

  let optionsToUse = R.or(options, []);

  if (G.isFunction(options)) optionsToUse = options();

  if (G.isNilOrEmpty(R.path([0, GC.FIELD_VALUE], optionsToUse))) {
    optionsToUse = R.tail(optionsToUse);
  }

  let placeholderValue = '';

  if (G.isNotNilAndNotEmpty(placeholder)) {
    placeholderValue = G.getWindowLocale(placeholder.key, placeholder.text);
  }

  return (
    <FormGroup>
      <Box m='5px 10px' width={232}>
        <CreatableReactSelect
          id={id}
          height={30}
          isMulti={true}
          menuPlacement='auto'
          options={optionsToUse}
          isDisabled={isDisabled}
          placeholder={placeholderValue}
          menuShouldScrollIntoView={false}
          shouldNotGetValueFromOptions={true}
          isOptionDisabled={handleDisableOptions}
          maxMenuHeight={filterSelectMaxMenuHeight}
          useMenuPortalTarget={useMenuPortalTarget}
          value={getMultiSelectValue(selectMultipleValue, stringValue)}
          closeMenuOnScroll={R.pathEq('edit_report_wrapper', ['target', 'id'])}
          onChange={(options) => handleChangeMultiSelect({ options, groupId, filterIndex })}
        />
      </Box>
    </FormGroup>
  );
};

export const renderField = (props) => {
  const { dataType } = props;

  const dataTypeCompMap = {
    date: DateComponent,
    time: TimeComponent,
    number: NumberComponent,
    string: StringComponent,
    boolean: BooleanComponent,
    dateOnly: DateOnlyComponent,
    reference: ReferenceComponent,
    'string:array': StringComponent,
    'string:select': SingleSelectComponent,
    selectMultiple: MultipleSelectComponent,
    selectMultipleCreatable: MultipleSelectComponentCreatable,
  };
  const FieldComponent = dataTypeCompMap[dataType];

  if (G.isNilOrEmpty(FieldComponent)) return null;

  return <FieldComponent {...props} />;
};
